import React, { useCallback, useState } from "react";
import { Popover } from "antd";
import { useCellViewModel } from "../hooks/useCellViewModel";
import { useStatusTrackerV2, useStatusTrackerModal } from "../hooks/useStatusTrackerV2";
import { isTaggableDate } from "../state/utils";
import { StatusTrackerCellView } from "../views/StatusTrackerCellView";
import { CoachFlagTagPopover } from "./CoachFlagTagPopover";

// Container for one day cell: computes the VM + wires the click to the shared modal, or — in
// tagging mode — to the coach flag popover instead.
//
// Every branch renders the same <StatusTrackerCellView> at the same position, so entering or
// leaving tagging mode only changes its props. Returning a different shape per mode made React
// tear down and rebuild every cell on the page — three antd tooltips each — which is what made
// the switch take seconds. Only the one open cell is wrapped, and only once it is open.
const DateCellInner = ({ uniqueKey }) => {
    const vm = useCellViewModel(uniqueKey);
    const { state } = useStatusTrackerV2();
    const { openCell } = useStatusTrackerModal();
    const [tagOpen, setTagOpen] = useState(false);

    // Stable across renders, so a cell whose mode did not change keeps identical props and the
    // memoised view skips its render entirely.
    const handleDetail = useCallback(() => openCell(uniqueKey), [openCell, uniqueKey]);
    // Already open: leave the toggle to the Popover, so one click cannot both open and close it.
    const handleTag = useCallback(() => setTagOpen(true), []);
    const noop = useCallback(() => {}, []);

    if (!uniqueKey) return null;

    const tagging = !!state.taggingMode;
    // A coach flag is manual, so every past or current cell is taggable — days with no player
    // data included. A future date is not: nothing has happened there yet.
    const taggable = tagging && isTaggableDate(uniqueKey.split("_")[1]);

    const cell = (
        <StatusTrackerCellView
            vm={vm}
            uniqueKey={uniqueKey}
            onOpen={tagging ? (taggable ? handleTag : noop) : handleDetail}
            forceClickable={taggable}
            className={tagging && !taggable ? "stv2-cell-untaggable" : ""}
        />
    );

    if (!taggable || !tagOpen) return cell;

    return (
        <Popover
            open
            onOpenChange={setTagOpen}
            trigger="click"
            placement="bottom"
            destroyTooltipOnHide
            content={<CoachFlagTagPopover uniqueKey={uniqueKey} onDone={() => setTagOpen(false)} />}
        >
            <div>{cell}</div>
        </Popover>
    );
};

export const DateCell = React.memo(DateCellInner);
